import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MessageCircle, MapPin, Mail, Phone, Shield, Wrench, Truck, Zap, Award, Factory } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useReveal } from "../hooks/useReveal";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookDemoForm } from "../components/BookDemoForm";
import ModelSpotlight from "../components/ModelSpotlight";
import ServiceMap from "../components/ServiceMap";
import { waLink } from "../lib/wa";

const SERVICE_AREAS = [
  "Panvel",
  "Mangaon",
  "Wadkhal",
  "Ambernath",
  "Shahpur",
  "Bhayandar",
  "Palghar",
  "Dhule",
];

const HERO_IMAGE =
  "https://res.cloudinary.com/doosan-bobcat/image/upload/ar_1.5,c_fill,f_auto,g_auto,q_auto,w_1600/v1773216447/bobcat-assets/alao-approved/in/products/backhoe-loaders/20260227-bobcat-alao-in-bhl-hero";

export default function Landing() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [all, setAll] = useState([]);
  const rootRef = useReveal([all.length, featured.length]);

  useEffect(() => {
    api.get("/models").then(({ data }) => {
      setAll(data);
      setFeatured(data.filter((m) => m.featured));
    });
  }, []);

  // Categories with counts for lineup section
  const categories = [...new Set(all.map((m) => m.category))].map((c) => ({
    name: c,
    count: all.filter((m) => m.category === c).length,
    sample: all.find((m) => m.category === c),
  }));

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={rootRef} className="bg-white text-[#111113]" data-testid="landing-page">
      <Navbar />

      {/* ========================= HERO ========================= */}
      <section className="relative min-h-[100vh] pt-28 md:pt-36 pb-12 overflow-hidden bg-white">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-white to-[#fafafa]" />

        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="overline mb-5" data-testid="hero-overline">
              Authorised Bobcat Dealer · Pan-India Service
            </div>
            <h1
              className="h1-hero text-5xl sm:text-6xl md:text-7xl lg:text-[92px] text-[#111113]"
              data-testid="hero-title"
              aria-label="Unleash Earth-Moving Power."
            >
              Unleash{" "}
              <br className="hidden sm:block" />
              Earth-Moving{" "}
              <br className="hidden sm:block" />
              <span className="text-[#ff6a13]">Power.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#6b6b72] leading-relaxed">
              Bhumi Equipments is a leading Authorised Dealer of Bobcat with
              Pan-India service support. Every model. Every attachment. Every
              service you need to finish the toughest job on earth.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/book-demo" className="btn-primary" data-testid="hero-book-demo-btn">
                Book a Demo <ArrowUpRight size={18} />
              </Link>
              <Link to="/models" className="btn-ghost" data-testid="hero-explore-btn">
                Explore Model Lineup <ArrowRight size={18} />
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {[
                { n: "18", l: "Models in India" },
                { n: "8", l: "Service Points" },
                { n: "Pan", l: "India Support" },
              ].map((k) => (
                <div key={k.l}>
                  <div className="font-display text-3xl md:text-4xl font-black text-[#111113]">
                    {k.n}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-[#6b6b72] mt-1">
                    {k.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 reveal">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute -inset-8 rounded-[3rem] bg-gradient-to-br from-[#ff6a13]/15 via-[#ff6a13]/5 to-transparent blur-3xl" />
              <img
                src={HERO_IMAGE}
                alt="Bobcat B900 Backhoe Loader on construction site"
                className="relative w-full h-auto rounded-[28px] shadow-2xl no-drag"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-16 py-5 border-y border-black/5 bg-white">
          <div className="marquee">
            <div className="marquee-track">
              {[...Array(2)].flatMap((_, j) =>
                [
                  "ONE TOUGH ANIMAL", "·", "AUTHORISED BOBCAT INDIA DEALER", "·",
                  "SKID-STEER LOADERS", "·", "COMPACT TRACK LOADERS", "·",
                  "BACKHOE LOADERS", "·", "MINI EXCAVATORS", "·", "ATTACHMENTS", "·",
                  "BUILT FOR INDIA", "·",
                ].map((w, i) => (
                  <span key={`${j}-${i}`} className={`mx-6 text-[11px] tracking-[0.3em] font-bold text-[#111113]/55 ${w === "ONE TOUGH ANIMAL" ? "italic" : ""}`}>
                    {w}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========================= MODEL LINEUP (CATEGORIES) ========================= */}
      <section id="models" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6 reveal">
            <div>
              <div className="overline mb-4">Model Lineup</div>
              <h2 className="h2-section text-4xl md:text-6xl max-w-2xl">
                Every Bobcat available in India.
                <br />
                <span className="text-[#6b6b72]">Now Pan-India.</span>
              </h2>
            </div>
          <Link to="/models" className="btn-ghost text-[#ff6a13] self-start md:self-end" data-testid="see-all-models-btn">
              See all models <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {categories.map((cat, i) => (
              <Link
                key={cat.name}
                to={`/models?category=${encodeURIComponent(cat.name)}`}
                className="reveal group relative overflow-hidden rounded-3xl bg-[#f5f5f7] hover:bg-white border border-transparent hover:border-[#ff6a13]/40 hover:shadow-2xl transition-all duration-500"
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`category-card-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#f5f5f7] to-[#ecedf0] flex items-center justify-center">
                  {cat.sample && (
                    <img
                      src={cat.sample.image}
                      alt={cat.name}
                      className="w-[88%] h-auto group-hover:scale-110 transition-transform duration-700 ease-out no-drag"
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#ff6a13] font-bold">
                    {cat.count} model{cat.count > 1 ? "s" : ""}
                  </div>
                  <div className="mt-2 font-display text-xl md:text-2xl font-bold text-[#111113]">
                    {cat.name}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#111113] group-hover:text-[#ff6a13] transition-colors">
                    Discover <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= SPOTLIGHT (Apple-style) ========================= */}
      {featured.length > 0 && <ModelSpotlight models={featured} />}

      {/* ========================= ABOUT (VC-memo style) ========================= */}
      <section id="about" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="reveal max-w-3xl">
            <div className="overline">About · Since 1947</div>
            <h2 className="h2-section text-4xl md:text-6xl mt-4">
              Bobcat invented the category.
              <br />
              <span className="text-[#6b6b72]">Bhumi brought it home.</span>
            </h2>
            <p className="mt-6 text-lg text-[#6b6b72] leading-relaxed">
              In 1947, brothers Eddie and Cy Melroe set out to solve a farmer's problem
              in Gwinner, North Dakota. By 1958, the Melroes had invented the world's
              first compact skid-steer loader — and with it, an entirely new industry.
              Seventy-eight years and one million machines later, Bobcat is the
              world's #1 compact equipment brand, operating across 90+ countries
              with manufacturing in the US, Europe, Korea and India.
            </p>
          </div>

          {/* VC-style metric grid */}
          <div className="mt-14 grid md:grid-cols-4 gap-px bg-[#e1e1e6] rounded-3xl overflow-hidden border border-[#e1e1e6] reveal">
            {[
              { icon: <Award />, k: "1947", l: "Year Bobcat founded · Gwinner, ND" },
              { icon: <Zap />, k: "1958", l: "Invented the compact skid-steer loader" },
              { icon: <Factory />, k: "1M+", l: "Machines built worldwide" },
              { icon: <Shield />, k: "90+", l: "Countries served globally" },
            ].map((s) => (
              <div key={s.k} className="bg-white p-8">
                <div className="text-[#ff6a13] mb-4">{s.icon}</div>
                <div className="font-display text-4xl font-black text-[#111113]">{s.k}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#6b6b72]">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 reveal">
              <div className="overline mb-4">Bobcat × India</div>
              <h3 className="h2-section text-3xl md:text-5xl">
                Building India's infrastructure,
                <br />
                one compact powerhouse at a time.
              </h3>
              <p className="mt-6 text-[#6b6b72] leading-relaxed text-lg">
                Bobcat's India operations — part of the Doosan Bobcat group — have deep
                local roots, with a dedicated R&D and manufacturing footprint engineered
                for Indian terrain, climate and cost realities. The <strong className="text-[#111113]">B900 CEV-V</strong>,
                <strong className="text-[#111113]"> EarthForce E18z and E33z</strong> were conceived for
                Indian jobsites — from urban metro works in Mumbai to the roadbed of
                the next national highway.
              </p>
              <p className="mt-4 text-[#6b6b72] leading-relaxed text-lg">
                <strong className="text-[#111113]">Bhumi Equipments</strong> is Bobcat's authorised distribution
                arm across Maharashtra — bringing every Bobcat model, every genuine
                attachment and every service procedure under one single, accountable
                roof. We don't just sell machines. We stake our name on their uptime.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/models" className="btn-primary">
                  View Full Lineup <ArrowUpRight size={16} />
                </Link>
                <button onClick={scrollToContact} className="btn-ghost" data-testid="about-talk-btn">
                  Talk to a Specialist <ArrowRight size={16} />
                </button>
              </div>
            </div>
            <div className="lg:col-span-5 reveal">
              <div className="rounded-3xl overflow-hidden aspect-[4/5] relative bg-[#0a0a0c] p-3">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,106,19,0.12),transparent_60%)]" />
                <div className="grid grid-cols-2 grid-rows-3 gap-3 h-full w-full relative">
                  {all.slice(0, 6).map((m) => (
                    <div
                      key={m.id}
                      className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center backdrop-blur"
                    >
                      <img
                        src={m.image}
                        alt={m.code}
                        className="w-[90%] h-auto no-drag"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                <div className="absolute bottom-4 left-4 right-4 z-10 bg-black/60 backdrop-blur rounded-xl p-4 text-white">
                  <div className="overline text-[#ff6a13]">The Full Lineup</div>
                  <div className="mt-1 font-display text-xl font-bold">Every Bobcat. One dealership.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================= SERVICE AREAS (with map) ========================= */}
      <section
        id="service-areas"
        className="relative py-24 md:py-36 bg-[#0a0a0c] text-white overflow-hidden"
      >
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="reveal">
            <div className="overline">Service Coverage · Pan-India</div>
            <h2 className="h2-section text-4xl md:text-7xl mt-4 max-w-4xl">
              Eight touch points.
              <br />
              One unstoppable network.
            </h2>
            <p className="mt-6 text-lg text-white/60 max-w-xl">
              Tap any pin or city — we'll route you straight to our team.
            </p>
          </div>

          <div className="mt-14 reveal">
            <ServiceMap />
          </div>
        </div>
      </section>

      {/* ========================= ETHICS & SERVICE STORY ========================= */}
      <section id="ethics" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="reveal max-w-3xl">
            <div className="overline">Our Ethics · Business · Service</div>
            <h2 className="h2-section text-4xl md:text-6xl mt-4">
              We don't sell machines.
              <br />
              <span className="text-[#6b6b72]">We back them — every hour they run.</span>
            </h2>
            <p className="mt-6 text-lg text-[#6b6b72] leading-relaxed">
              Twenty-five years on Indian jobsites have taught us one thing:
              <em> a Bobcat is only as good as the dealership behind it.</em> That's why
              every contract we sign, every part we stock, and every technician we
              dispatch is held to a single standard — get the customer back to work,
              fast and fair.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5 reveal">
            {[
              { t: "Integrity First", d: "No hidden fees. No grey-market parts. Written estimates before any service work begins." },
              { t: "Genuine Bobcat", d: "100% OEM parts sourced direct from Doosan Bobcat India. We don't compromise — and neither should your machine." },
              { t: "Uptime is Promise", d: "WhatsApp response within 15 minutes. On-site technicians dispatched same day across our network." },
              { t: "Customer for Life", d: "Annual Service Contracts and Extended Warranty (up to 5 years) — we stay with your machine, year after year." },
            ].map((v) => (
              <div key={v.t} className="p-7 rounded-3xl bg-[#f5f5f7] border border-transparent hover:border-[#ff6a13]/40 transition-all">
                <div className="font-display text-xl font-bold text-[#111113]">{v.t}</div>
                <p className="mt-3 text-sm text-[#6b6b72] leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= PARTS & SERVICE PROMO ========================= */}
      <section className="py-24 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-5">
          <Link
            to="/parts-service"
            className="reveal md:col-span-2 group p-10 md:p-14 rounded-3xl bg-white border border-black/5 hover:border-[#ff6a13]/40 transition-all"
            data-testid="parts-service-card"
          >
            <Wrench className="text-[#ff6a13]" size={28} />
            <h3 className="h2-section text-3xl md:text-5xl mt-6">
              Parts & Service.
              <br />
              <span className="text-[#6b6b72]">The reason Bobcat outlives everything else.</span>
            </h3>
            <p className="mt-5 text-[#6b6b72] max-w-2xl">
              Genuine parts stocked in Panvel. Certified technicians at every touch point.
              Maintenance plans, warranty, and on-site service — built around your uptime.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-[#ff6a13] font-semibold">
              Explore Parts & Service <ArrowUpRight size={16} />
            </span>
          </Link>

          <Link
            to="/attachments"
            className="reveal group p-10 rounded-3xl bg-[#111113] text-white hover:bg-[#ff6a13] transition-colors relative overflow-hidden"
            data-testid="attachments-card"
          >
            <Truck className="text-[#ff6a13] group-hover:text-white" size={26} />
            <h3 className="h2-section text-2xl md:text-3xl mt-6">
              80+ Genuine Attachments.
            </h3>
            <p className="mt-3 text-white/60 group-hover:text-white/90 text-sm">
              Turn one machine into many. Buckets, breakers, augers, forks, brooms and more.
            </p>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold">
              Browse Attachments <ArrowUpRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* ========================= BOOK-A-DEMO ========================= */}
      <section id="book-demo-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="reveal text-center">
            <div className="overline">Book a Demo</div>
            <h2 className="h2-section text-4xl md:text-6xl mt-4 max-w-3xl mx-auto">
              Feel the machine
              <br />
              before you invest.
            </h2>
            <p className="mt-5 text-lg text-[#6b6b72] max-w-xl mx-auto">
              Schedule a live demonstration at your site. No obligations.
            </p>
          </div>
          <div className="mt-14 reveal">
            <BookDemoForm />
          </div>
        </div>
      </section>

      {/* ========================= CONTACT ========================= */}
      <section id="contact" className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12">
          <div className="reveal">
            <div className="overline">Contact</div>
            <h2 className="h2-section text-4xl md:text-6xl mt-4">
              Let's build
              <br />
              something tough.
            </h2>
            <p className="mt-6 text-lg text-[#6b6b72] max-w-lg">
              Talk to our team directly. WhatsApp is fastest for immediate queries.
            </p>
            <a
              href={waLink({ source: "contact_section", message: "Hi Bhumi Equipments, I'd like to connect." })}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-3 btn-primary text-base"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle size={20} /> Chat on WhatsApp
            </a>
          </div>

          <div className="reveal">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-8 rounded-3xl bg-white border border-black/5">
                <MapPin className="text-[#ff6a13]" />
                <div className="mt-4 font-bold">Head Office</div>
                <div className="mt-2 text-sm text-[#6b6b72] leading-relaxed">
                  #13, Ground Floor, National Paradise, Plot No. 290/1, Takka,
                  Panvel – 410 206
                </div>
              </div>
              <div className="p-8 rounded-3xl bg-white border border-black/5">
                <Phone className="text-[#ff6a13]" />
                <div className="mt-4 font-bold">Call Us</div>
                <a href="tel:+919167632596" className="mt-2 block text-sm text-[#6b6b72] hover:text-[#ff6a13]">
                  +91 91676 32596
                </a>
              </div>
              <div className="p-8 rounded-3xl bg-white border border-black/5">
                <Mail className="text-[#ff6a13]" />
                <div className="mt-4 font-bold">Email</div>
                <a href="mailto:info@bhumiequipments.com" className="mt-2 block text-sm text-[#6b6b72] hover:text-[#ff6a13]">
                  info@bhumiequipments.com
                </a>
              </div>
              <div className="p-8 rounded-3xl bg-[#0a0a0c] text-white">
                <div className="overline text-[#ff6a13]">Dealer Principal</div>
                <div className="mt-4 font-display text-2xl font-bold">Vaijanath Birajdar</div>
                <div className="mt-2 text-sm text-white/60">
                  Bhumi Equipments · Authorised Dealer of Bobcat®
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating WhatsApp */}
      <a
        href={waLink({ source: "floating_btn", message: "Hi Bhumi Equipments, I'd like to connect." })}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 z-40 bg-[#25D366] text-white rounded-full w-14 h-14 flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
        aria-label="WhatsApp"
        data-testid="floating-whatsapp-btn"
      >
        <MessageCircle size={26} />
      </a>
    </div>
  );
}
